import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Upload, FileText, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Dialog per l'import di Fatture Elettroniche XML SDI (export Danea Easyfatt).
 * Accetta un singolo file .xml o un .zip contenente più .xml.
 */
export const ImportXmlDialog = ({ open, onOpenChange, onImported }) => {
  const [file, setFile] = useState(null);
  const [direction, setDirection] = useState('attive');
  const [skipDup, setSkipDup] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setFile(null);
    setDirection('attive');
    setSkipDup(true);
    setResult(null);
    setBusy(false);
  };

  const handleClose = (val) => {
    if (busy) return;
    if (!val) reset();
    onOpenChange(val);
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Seleziona un file XML o ZIP');
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('direction', direction);
      formData.append('skip_duplicates', skipDup ? 'true' : 'false');

      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('evotech_token');
      const resp = await fetch(`${API_URL}/api/treasury/invoices/import-xml`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data.detail || 'Errore import');
      }
      setResult(data);
      if (data.imported_count > 0) {
        toast.success(`${data.imported_count} fattur${data.imported_count === 1 ? 'a' : 'e'} importat${data.imported_count === 1 ? 'a' : 'e'}`);
        onImported?.();
      } else if (data.skipped_count > 0) {
        toast.info('Nessuna nuova fattura importata (tutte duplicate)');
      } else {
        toast.error('Import fallito: nessuna fattura elaborata');
      }
    } catch (e) {
      toast.error(e.message || 'Errore import');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Importa Fatture Elettroniche (XML SDI)
          </DialogTitle>
          <DialogDescription>
            Carica un file XML SDI (o ZIP con più XML) esportato da Danea Easyfatt.
            Le rate vengono estratte automaticamente da &lt;DatiPagamento&gt; quando presenti.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-4 py-3">
            <div className="space-y-2">
              <Label>File XML o ZIP *</Label>
              <input
                type="file"
                accept=".xml,.zip"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-4 file:rounded file:border-0 file:bg-slate-900 file:text-white hover:file:bg-slate-800 file:cursor-pointer"
                data-testid="xml-import-file-input"
                disabled={busy}
              />
              {file && (
                <p className="text-xs text-slate-500 flex items-center gap-1">
                  <FileText className="w-3 h-3" /> {file.name} ({(file.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Tipo fatture *</Label>
              <Select value={direction} onValueChange={setDirection} disabled={busy}>
                <SelectTrigger data-testid="xml-import-direction">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="attive">Attive (emesse a clienti → Entrate)</SelectItem>
                  <SelectItem value="passive">Passive (ricevute da fornitori → Uscite)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="skip-dup"
                checked={skipDup}
                onCheckedChange={(v) => setSkipDup(!!v)}
                disabled={busy}
                data-testid="xml-import-skip-dup"
              />
              <Label htmlFor="skip-dup" className="cursor-pointer text-sm">
                Salta fatture già importate (stesso numero + data + direzione)
              </Label>
            </div>

            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-3">
              <p className="font-medium mb-1">Note:</p>
              <ul className="list-disc ml-4 space-y-0.5">
                <li>Se il file contiene &lt;DatiPagamento&gt; le rate vengono create con date effettive dell&apos;XML.</li>
                <li>Le anagrafiche mancanti vengono create automaticamente (matching per P.IVA o nome).</li>
                <li>File firmati .p7m non supportati: estrai prima l&apos;XML.</li>
                <li>Note di credito (TD04) importate con importo negativo.</li>
              </ul>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-3 py-3 max-h-[50vh] overflow-y-auto">
            <div className="grid grid-cols-4 gap-3 text-center">
              <StatBox label="Totale" val={result.total_parsed} color="slate" />
              <StatBox label="Importate" val={result.imported_count} color="emerald" />
              <StatBox label="Saltate" val={result.skipped_count} color="amber" />
              <StatBox label="Errori" val={result.error_count} color="red" />
            </div>

            {result.imported?.length > 0 && (
              <div className="border rounded">
                <div className="bg-emerald-50 px-3 py-2 border-b flex items-center gap-2 text-sm font-medium text-emerald-800">
                  <CheckCircle2 className="w-4 h-4" /> Fatture importate
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left px-3 py-1">Numero</th>
                      <th className="text-left px-3 py-1">Data</th>
                      <th className="text-left px-3 py-1">Anagrafica</th>
                      <th className="text-right px-3 py-1">Totale</th>
                      <th className="text-center px-3 py-1">Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.imported.map(r => (
                      <tr key={r.id} className="border-b last:border-0" data-testid={`imported-row-${r.id}`}>
                        <td className="px-3 py-1 font-mono">{r.numero}</td>
                        <td className="px-3 py-1 font-mono">{r.data}</td>
                        <td className="px-3 py-1">{r.party}</td>
                        <td className="px-3 py-1 text-right font-mono">€ {r.totale.toFixed(2)}</td>
                        <td className="px-3 py-1 text-center">{r.rate_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {result.skipped?.length > 0 && (
              <div className="border rounded">
                <div className="bg-amber-50 px-3 py-2 border-b flex items-center gap-2 text-sm font-medium text-amber-800">
                  <AlertCircle className="w-4 h-4" /> Fatture saltate
                </div>
                <ul className="text-xs px-3 py-2 space-y-1">
                  {result.skipped.map((s, i) => (
                    <li key={i} className="text-slate-600">
                      <span className="font-mono">{s.numero}</span> del {s.data} — {s.party} — {s.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {result.errors?.length > 0 && (
              <div className="border rounded border-red-200">
                <div className="bg-red-50 px-3 py-2 border-b flex items-center gap-2 text-sm font-medium text-red-800">
                  <AlertCircle className="w-4 h-4" /> Errori
                </div>
                <ul className="text-xs px-3 py-2 space-y-1">
                  {result.errors.map((e, i) => (
                    <li key={i} className="text-red-700">
                      <span className="font-mono">{e.numero || '—'}</span>: {e.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {result ? (
            <>
              <Button variant="outline" onClick={reset} data-testid="xml-import-again">Nuovo import</Button>
              <Button onClick={() => handleClose(false)} data-testid="xml-import-close">Chiudi</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => handleClose(false)} disabled={busy}>Annulla</Button>
              <Button onClick={handleImport} disabled={busy || !file} data-testid="xml-import-submit">
                {busy ? 'Import in corso...' : 'Importa'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const StatBox = ({ label, val, color }) => {
  const bg = {
    slate: 'bg-slate-50 text-slate-800 border-slate-200',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    red: 'bg-red-50 text-red-800 border-red-200',
  }[color] || 'bg-slate-50 text-slate-800 border-slate-200';
  return (
    <div className={`border rounded p-3 ${bg}`}>
      <p className="text-xs uppercase tracking-wider font-bold opacity-70">{label}</p>
      <p className="text-2xl font-bold mt-1">{val}</p>
    </div>
  );
};
